// Self-service drop-off and pick-up (/guardian/handoff/:type). The parent scans the door code a
// staff member shows (or, where the school allows it, asks without scanning), picks the children,
// and for a pick-up takes a photo and signs, all on their own phone. Each event is signed with this phone's key (registered with "My pickup code") and
// sealed into the site's chain; the door code proves a staff member was at the door.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Avatar, Banner, Button, Checkbox, Icon, ScannerFrame, Spinner, SuccessScreen } from "@vavr/ui";
import { buildPreImage, StrokeRecorder } from "@vavr/client";
import { canonicalize, parseDoorCode, sha256Hex, signCanonical, strokeHash, uuidv7, type PreImage } from "@vavr/core";
import { api, ApiError, navigate } from "../shared/index.ts";
import { time, fullName } from "../shared/format.ts";
import { openCamera, stopCamera, type Camera, type Photo } from "../kiosk/camera.ts";
import { makeQrDecoder } from "../kiosk/qr-scan.ts";
import { loadFaceDetector } from "../kiosk/face.ts";
import { gateStart, gateStep, faceRecord, GATE_HINT, type GateState } from "../kiosk/face-gate.ts";
import { SignaturePad } from "../kiosk/flow/SignaturePad.tsx";
import { useGuardian, G } from "./context.tsx";
import { holderKey, passFor } from "./pass.ts";
import { allowsAppRequest, handoffBlock, todayStatus, whereToday, type GuardianSite } from "./status.ts";
import "../kiosk/kiosk.css";
import "./handoff.css";

type Kind = "dropoff" | "pickup";

/** A door code handed over by an NFC tap: the pickup opens with it instead of the camera. */
export const tappedDoor: { code: string | null } = { code: null };
interface Door {
  /** The self-service door tablet's code: this asks for the children (a pickup request) instead of handing over. */
  kiosk: boolean; device_name: string;
  /** Without scanning: no door code; whoever has the child hands them over or confirms the drop-off. */
  manual?: boolean;
  code: string; site: GuardianSite; staff_name: string | null; staff_id: string | null; device_id: string; window: number; sig: string;
  dropoff_signature: "required" | "optional" | "off"; attestation: string;
}
type Step = "scan" | "children" | "sign" | "sending" | "done";

export function Handoff({ kind }: { kind: Kind }) {
  const { overview, kv, reload } = useGuardian();
  const [step, setStep] = useState<Step>("scan");
  const [door, setDoor] = useState<Door | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ names: string[]; at: number } | null>(null);
  const [photo, setPhoto] = useState<{ p: Photo; face: PreImage["face"] } | null>(null);
  const recorder = useRef<StrokeRecorder | null>(null);
  const [signed, setSigned] = useState(false);
  const [clearKey, setClearKey] = useState(0);
  const verb = kind === "pickup" ? "Pick up" : "Drop off";
  // Opened by a tap: use the code the staff phone gave instead of scanning.
  useEffect(() => {
    const code = tappedDoor.code;
    if (!code) return;
    tappedDoor.code = null;
    void onCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCode(text: string) {
    const parsed = parseDoorCode(text);
    if (!parsed) { setError("That is not a Vavr door code. Ask the staff member at the door to show theirs."); return; }
    const site = overview.sites.find((s) => s.site.id === parsed.payload.site_id);
    if (!site) { setError("That door code is for a school or center your children are not at."); return; }
    // The code says what it is for (the tablet's Drop off or Collect): follow it.
    const purpose = parsed.payload.purpose;
    if (purpose && purpose !== kind) { tappedDoor.code = text; navigate(`${G}/handoff/${purpose}`); return; }
    try {
      const who = await api<{ kiosk?: boolean; device_name?: string; staff_name: string | null; staff_id: string | null; device_id: string; dropoff_signature: Door["dropoff_signature"]; attestation_pickup: string; attestation_dropoff: string }>(
        "POST", `/api/v1/guardian/sites/${site.site.id}/door`, { door: text });
      setDoor({
        kiosk: !!who.kiosk, device_name: who.device_name ?? "the door tablet",
        code: text, site, staff_name: who.staff_name, staff_id: who.staff_id, device_id: who.device_id, window: parsed.payload.window, sig: parsed.sig,
        dropoff_signature: who.dropoff_signature, attestation: kind === "pickup" ? who.attestation_pickup : who.attestation_dropoff,
      });
      setChosen(preselect(site, kind));
      setError(null);
      setStep("children");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not check the door code. Check your connection and scan again.");
    }
  }

  const needsSignature = kind === "pickup" || door?.dropoff_signature === "required";

  // Without a code (where the school allows it): the parent asks from the phone; whoever has the
  // child hands them over, or the class teacher confirms the drop-off.
  const manualSite = overview.sites.find((x) => x.children.length && allowsAppRequest(x, kind));
  async function startManual() {
    const site = manualSite;
    if (!site) return;
    try {
      const st = await api<{ pickup: boolean; dropoff: boolean; dropoff_signature: Door["dropoff_signature"]; attestation_pickup: string; attestation_dropoff: string }>("GET", `/api/v1/guardian/sites/${site.site.id}/request-settings`);
      if (!st[kind]) { setError(kind === "pickup" ? "Your school asks you to scan the door code to pick up." : "Your school asks you to scan the door code to drop off."); reload(); return; }
      setDoor({
        kiosk: false, manual: true, device_name: "", code: "", site, staff_name: null, staff_id: null, device_id: "", window: 0, sig: "",
        dropoff_signature: st.dropoff_signature, attestation: kind === "pickup" ? st.attestation_pickup : st.attestation_dropoff,
      });
      setChosen(preselect(site, kind));
      setError(null);
      setStep("children");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not reach the school. Check your connection and try again.");
    }
  }

  // One send at a time: a second tap before the screen changes would ask twice.
  const sending = useRef(false);
  async function submit() {
    if (!door || sending.current) return;
    sending.current = true;
    setStep("sending");
    setError(null);
    try {
      const siteId = door.site.site.id;
      const { cached } = await passFor(kv, siteId, (site, holder) => api("POST", `/api/v1/guardian/sites/${site}/pass`, { holder_key: holder }), Date.now(), true);
      if (!cached?.key_id) throw new Error("This phone could not register its key. Check your connection.");
      const keys = await holderKey(kv);
      const upload = async (bytes: Uint8Array, type: string) => {
        const sha = await sha256Hex(bytes);
        await api("PUT", `/api/v1/guardian/sites/${siteId}/blobs/${sha}`, undefined, { raw: bytes, type });
        return sha;
      };
      const photoSha = photo ? await upload(photo.p.bytes, "image/jpeg") : null;
      let strokeSha: string | null = null;
      if (needsSignature && recorder.current && signed) {
        const stroke = recorder.current.toStroke();
        await upload(new TextEncoder().encode(canonicalize(stroke)), "application/json");
        strokeSha = await strokeHash(stroke);
      }
      // Pickups and drop-offs are requests: a member of staff hands the child over or confirms
      // they have them. A request always has a group, which the door tablet and staff phone follow.
      const request = true;
      const group = uuidv7();
      const events = [];
      for (const childId of chosen) {
        const pre = buildPreImage({
          siteId, deviceId: cached.key_id, staffId: request ? null : door.staff_id, type: kind === "pickup" ? "pickup_request" : "dropoff_request", childId, groupId: group, adult: { id: door.site.adult_id },
          authMethod: door.manual ? "app" : "door_qr", photoSha256: photoSha, face: photo?.face ?? null, strokeSha256: strokeSha, attestation: door.attestation, monoStart: performance.timeOrigin,
        });
        if (!door.manual) pre.data = { door_device: door.device_id, door_window: door.window, door_sig: door.sig };
        events.push({ pre, sig: await signCanonical(keys.privateKey, pre) });
      }
      if (door.manual) await api("POST", `/api/v1/guardian/sites/${siteId}/request`, { events });
      else await api("POST", `/api/v1/guardian/sites/${siteId}/handoff`, { door: door.code, events });
      setDone({ names: door.site.children.filter((c) => chosen.has(c.id)).map((c) => c.first), at: Date.now() });
      setStep("done");
      reload();
    } catch (e) {
      // Already picked up, already at school, or already asked (409 already_done): back to the
      // children, refreshed, so the parent sees which one and why.
      const twice = e instanceof ApiError && e.status === 409;
      setError(e instanceof ApiError ? e.message : (e as Error).message);
      if (twice) { reload(); setChosen(new Set()); }
      setStep(needsSignature && !twice ? "sign" : "children");
    } finally { sending.current = false; }
  }

  const total = needsSignature ? 3 : 2;
  const kidWord = (n: number) => (n === 1 ? "1 child" : `${n} children`);

  if (step === "scan") {
    return (
      <div className="pmu-g-handoff">
        <DoorScanner
          onText={onCode} kind={kind} onClose={() => navigate(G)}
          status={error && <Banner tone="warning" title={error} />}
          extra={manualSite ? <Button variant="secondary" icon={kind === "pickup" ? "leave" : "arrive"} onClick={() => void startManual()}>{verb} without scanning</Button> : undefined}
        />
      </div>
    );
  }

  if (step === "children" && door) {
    // The overview as it is now (reloaded after a refusal), not as it was when the code was scanned.
    const here = overview.sites.find((x) => x.site.id === door.site.site.id) ?? door.site;
    return (
      <div className="pmu-g-handoff pmu-g-handoff--flow">
        <Steps at={2} of={total} />
        <div className="pmu-g-handoff__who">
          <span className="pmu-g-handoff__who-icon"><Icon name="check" /></span>
          {door.manual
            ? <span><strong>No code needed</strong><br /><span className="pmu-g-handoff__who-site">{door.site.site.name}: {kind === "pickup" ? "we tell whoever has your child" : "the class teacher confirms when your child is with them"}</span></span>
            : door.kiosk
            ? <span><strong>Door tablet</strong> found<br /><span className="pmu-g-handoff__who-site">{door.site.site.name}: we tell whoever has your child</span></span>
            : <span><strong>{door.staff_name}</strong> is at the door<br /><span className="pmu-g-handoff__who-site">{door.site.site.name}</span></span>}
        </div>
        <h2 className="pmu-g-handoff__title">{door.kiosk ? "Who are you collecting?" : kind === "pickup" ? "Who are you picking up?" : "Who are you dropping off?"}</h2>
        {error && <Banner tone="warning" title={error} />}
        <div className="pmu-g-handoff__kids">
          {here.children.map((c) => {
            const st = todayStatus(c, here.recent, here.site.tz);
            const blocked = handoffBlock(here, c, kind);
            return (
              <Checkbox
                key={c.id} className="pmu-g-pick" disabled={!!blocked}
                label={<span className="pmu-g-pick__who"><Avatar kind="child" size="md" name={fullName(c)} /><span className="pmu-g-pick__name">{c.first}<span className="pmu-sr-only">{`: ${verb.toLowerCase()}`}</span></span></span>}
                description={blocked ?? st.label}
                checked={!blocked && chosen.has(c.id)}
                onCheckedChange={(on) => setChosen((s) => { const n = new Set(s); on ? n.add(c.id) : n.delete(c.id); return n; })}
              />
            );
          })}
        </div>
        <div className="pmu-g-dock">
          <Button fullWidth size="lg" iconEnd={needsSignature ? "chevron-right" : undefined} icon={needsSignature ? undefined : "check"} disabled={!chosen.size} onClick={() => (needsSignature ? setStep("sign") : void submit())}>
            {needsSignature ? "Continue" : `${verb} ${kidWord(chosen.size)}`}
          </Button>
          <Button variant="ghost" icon="scan" onClick={() => { setStep("scan"); setDoor(null); }}>Scan again</Button>
        </div>
      </div>
    );
  }

  if (step === "sign" && door) {
    return (
      <div className="pmu-g-handoff pmu-g-handoff--flow">
        <Steps at={3} of={total} />
        <h2 className="pmu-g-handoff__title">{kind === "pickup" ? "Photo and signature" : "Sign to confirm"}</h2>
        {kind === "pickup" && <Selfie onPhoto={setPhoto} photo={photo?.p ?? null} />}
        {kind === "pickup" && !photo && <p className="pmu-small pmu-muted pmu-g-center">Your photo is taken automatically when your face is in view.</p>}
        <div className="pmu-g-handoff__signcard">
          <p className="pmu-g-handoff__attest">{door.attestation}</p>
          <div className="pmu-g-handoff__pad">
            <SignaturePad label="Sign here with your finger" clearKey={clearKey} onRecorder={(r) => { recorder.current = r; }} onChange={(_, enough) => setSigned(enough)} />
          </div>
        </div>
        {error && <Banner tone="warning" title={error} />}
        <div className="pmu-g-dock pmu-g-dock--row">
          <Button variant="secondary" onClick={() => { setClearKey((k) => k + 1); setSigned(false); }}>Clear</Button>
          <Button size="lg" icon="check" disabled={!signed || (kind === "pickup" && !photo)} onClick={() => void submit()}>Send request</Button>
        </div>
      </div>
    );
  }

  if (step === "sending") return <div className="pmu-center pmu-g-handoff__sending"><Spinner size="lg" label="Sending" /><p className="pmu-muted">Sealing the record…</p></div>;

  return (
    <div className="pmu-g-handoff">
      <SuccessScreen
        title={done && kind === "dropoff" ? "We've told the teacher" : done && (door?.kiosk || door?.manual) ? "We've told the teacher" : done && kind === "pickup" ? `Sent to ${door?.staff_name ?? "the staff member"}` : "Done"}
        description={done && kind === "dropoff" ? `${done.names.join(" and ")} ${done.names.length > 1 ? "are" : "is"} checked in as soon as ${door?.staff_name ?? "the teacher"} confirms. You get a notification.`
          : done && door?.kiosk ? `Please wait at the front door. The door tablet shows who is bringing ${done.names.join(" and ")}.`
          : done && door?.manual ? `Come to the front door. We tell you here who is bringing ${done.names.join(" and ")}, or which room to collect from.`
          : done && kind === "pickup" ? `${door?.staff_name ?? "They"} will hand ${done.names.join(" and ")} over to you now.`
          : done && door ? `${time(done.at, door.site.site.tz)} · handed ${kind === "pickup" ? "over by" : "to"} ${door.staff_name}. Their teacher has been told.` : undefined}
        actions={<Button fullWidth size="lg" onClick={() => navigate(G)}>Back to home</Button>}
      />
    </div>
  );
}

/**
 * The children to tick at the start: those this makes sense for (at school for a pick-up, not yet
 * arrived for a drop-off), never one already picked up, already at school or already asked for.
 */
function preselect(site: GuardianSite, kind: Kind): Set<string> {
  const open = site.children.filter((c) => !handoffBlock(site, c, kind));
  // A pickup: those at school. A drop-off: those with nothing recorded today (not one already collected).
  const likely = open.filter((c) => whereToday(c, site) === (kind === "pickup" ? "here" : "none"));
  // Nothing recorded today for any child: the school may not record arrivals, so offer every child.
  const untracked = site.children.every((c) => !todayStatus(c, site.recent, site.site.tz).event) && !(site.waiting ?? []).length;
  return new Set((likely.length || !untracked ? likely : open).map((c) => c.id));
}

/** "Step 2 of 3": a short progress bar so the parent knows how much is left. */
function Steps({ at, of }: { at: number; of: number }) {
  return (
    <div className="pmu-g-steps" role="img" aria-label={`Step ${at} of ${of}`}>
      {Array.from({ length: of }, (_, i) => <span key={i} data-on={i < at} />)}
    </div>
  );
}

/** Back camera with live QR decoding. */
function DoorScanner({ onText, kind, onClose, status, extra }: { onText: (t: string) => Promise<void>; kind: Kind; onClose: () => void; status?: ReactNode; extra?: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [cam, setCam] = useState<Camera | null>(null);
  const busy = useRef(false);
  useEffect(() => {
    let c: Camera | null = null;
    let live = true;
    openCamera("environment").then((x) => { c = x; if (live) setCam(x); else stopCamera(x); });
    return () => { live = false; stopCamera(c); };
  }, []);
  useEffect(() => {
    if (!cam?.video) return;
    const v = cam.video;
    v.className = "pmu-g-handoff__video";
    box.current?.replaceChildren(v);
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    makeQrDecoder().then((decode) => {
      const tick = async () => {
        if (!live) return;
        if (!busy.current) {
          const t = await decode(v).catch(() => null);
          if (t) { busy.current = true; await onText(t); busy.current = false; }
        }
        timer = setTimeout(tick, 200);
      };
      void tick();
    });
    return () => { live = false; clearTimeout(timer); };
  }, [cam, onText]);
  return (
    <ScannerFrame
      title={kind === "pickup" ? "Pick up: scan the door code" : "Drop off: scan the door code"}
      hint="Tap 'Have the app?' on the door tablet, or ask the member of staff at the door to show their code."
      onClose={onClose} closeLabel="Cancel"
      overlay={!cam && <Spinner size="lg" label="Opening the camera" />}
      status={<>{cam?.error && <Banner tone="warning" title={cam.error} />}{status}</>}
      actions={<>{extra}<Button variant="secondary" onClick={onClose}>Cancel</Button></>}
    >
      <div ref={box} className="pmu-g-handoff__viewport" />
    </ScannerFrame>
  );
}

/**
 * Front camera: the photo is taken only when exactly one face is in view (detection only, never
 * recognition). There is no blind shutter: the helper button also requires a face in the frame.
 */
function Selfie({ onPhoto, photo }: { onPhoto: (x: { p: Photo; face: PreImage["face"] }) => void; photo: Photo | null }) {
  const box = useRef<HTMLDivElement>(null);
  const [cam, setCam] = useState<Camera | null>(null);
  const [hint, setHint] = useState("Look at the camera");
  const [manual, setManual] = useState(false);
  const [broken, setBroken] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const gateRef = useRef<GateState>(gateStart);
  useEffect(() => {
    let c: Camera | null = null;
    let live = true;
    openCamera("user").then((x) => { c = x; if (live) setCam(x); else stopCamera(x); });
    const t = setTimeout(() => setManual(true), 6000);
    return () => { live = false; clearTimeout(t); stopCamera(c); };
  }, []);
  useEffect(() => {
    if (!cam?.video || photo) return;
    const v = cam.video;
    v.className = "pmu-g-handoff__video pmu-g-handoff__video--mirror";
    box.current?.replaceChildren(v);
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    loadFaceDetector().then((det) => {
      if (!det) { setBroken(true); return; }
      const tick = async () => {
        if (!live) return;
        const now = performance.now();
        const gate = gateStep(gateRef.current, det.detect(v, now), v.videoHeight || 1, now);
        gateRef.current = gate;
        setHint(GATE_HINT[gate.status]);
        if (gate.status === "ready" && gate.best) {
          const p = await cam.capture();
          if (p && live) { onPhoto({ p, face: faceRecord(gate.best, p.scale, p.width, p.height) }); return; }
        }
        timer = setTimeout(tick, 150);
      };
      void tick();
    });
    return () => { live = false; clearTimeout(timer); };
  }, [cam, photo, onPhoto]);
  useEffect(() => {
    if (!photo) return;
    const u = URL.createObjectURL(new Blob([photo.bytes as BlobPart], { type: "image/jpeg" }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [photo]);
  const takeNow = async () => {
    const g = gateRef.current;
    if (!cam || !g.best || g.status === "none" || g.status === "many") { setHint("No face found: look at the camera"); return; }
    const p = await cam.capture();
    if (p) onPhoto({ p, face: faceRecord(g.best, p.scale, p.width, p.height) });
  };
  return (
    <div className="pmu-g-handoff__selfie">
      {photo && url ? <img src={url} alt="Your photo for this pickup" className="pmu-g-handoff__shot" /> : <div ref={box} className="pmu-g-handoff__viewport pmu-g-handoff__viewport--selfie" data-face={hint === GATE_HINT.holding}>{!cam && <Spinner label="Opening the camera" />}</div>}
      {broken ? <Banner tone="warning" title="The face check could not start">Close the app and open it again. A pickup needs a photo with your face in it.</Banner> : <p className="pmu-small">{photo ? "Photo taken" : hint}</p>}
      {!photo && !broken && manual && cam?.video && <Button size="sm" variant="secondary" icon="camera" onClick={() => void takeNow()}>Take photo</Button>}
    </div>
  );
}
